import React, { useCallback, useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { apiService } from '../../services/apiService';
import { CallInsightsAnalysis } from '../adherence/types';
import type { SalesPlaybookUsage } from './PlaybookUsagePanel';
import SalesIntelligenceV2Section from './SalesIntelligenceV2Section';
import KnowledgeBaseSection from './KnowledgeBaseSection';
import PlaybookSection from './PlaybookSection';
import MultiPartyInsights from './MultiPartyInsights';
import CallSummaryTab, { type DetailsSection } from './summary/CallSummaryTab';
import { hasParticipants, isMultiPartyCall } from './multiPartyFormat';
import { Button } from '../ui/Button';
import { ProgressBar } from '../ui/ProgressBar';

interface SpeechRateAlert {
  total_words: number;
  words_per_minute: number;
  timestamp: string;
}

interface CallInsightsTabProps {
  sessionId: string;
  callInsights?: CallInsightsAnalysis;
  salesPlaybookUsage?: SalesPlaybookUsage;
  speechRateAlerts: SpeechRateAlert[];
  isAdmin: boolean;
  onAnalysisUpdated: () => void;
  onNavigateToTimestamp?: (ts: string) => void;
  /** Who was on the call, editable; shown first in Details when the call has participant identity. */
  participantsEditor?: React.ReactNode;
}

const TOTAL_DIMENSIONS = 6;
const MAX_POLL_ATTEMPTS = 100;

const DIMENSION_LABELS: Record<string, string> = {
  objection_faq: 'Objections / FAQs',
  landmine: 'Landmines',
  competitor: 'Competitors',
  playbook: 'Playbook',
  sales_intelligence: 'Sales Intelligence',
  tone_analysis: 'Tone Analysis',
};

// TODO(cleanup): unreferenced today. Kept rather than deleted so removing it is a
// deliberate, reviewed change instead of a side effect of the lint pass.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function formatGeneratedAt(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return dateStr;
  }
}

// Tailwind tone for a 0-100 score: green >=80, amber >=60, red below.
function scoreTone(score: number): string {
  if (score >= 80) return 'bg-success-tint text-success border-success';
  if (score >= 60) return 'bg-warning-tint text-warning border-warning';
  return 'bg-error-tint text-error border-error';
}

/**
 * Headline Call Quality Score badge. The score is the same per-call composite
 * the superadmin portal aggregates (handling items + playbook, pooled), stored
 * on the call by post_call_analysis. Hidden when the call had nothing to score.
 */
const CallQualityBadge: React.FC<{
  score?: number | null;
  breakdown?: CallInsightsAnalysis['call_quality_breakdown'];
}> = ({ score, breakdown }) => {
  if (score === null || score === undefined) return null;
  const parts: string[] = [`${breakdown?.handling_item_count ?? 0} handling item(s)`];
  if (breakdown?.playbook_used) {
    parts.push(`playbook ${breakdown.playbook_score ?? '—'}`);
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${scoreTone(
        score
      )}`}
      title={`Call Quality Score — pooled handling + playbook (${parts.join(', ')})`}
    >
      <span className="uppercase tracking-wider text-[10px] font-bold opacity-70">Call quality</span>
      {score}%
    </span>
  );
};

interface SectionAccordionProps {
  label: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const SectionAccordion: React.FC<SectionAccordionProps> = ({
  label,
  isExpanded,
  onToggle,
  children,
}) => (
  <div className="bg-surface rounded-xl overflow-hidden shadow-sm border border-hairline">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-4 bg-surface-subtle hover:bg-surface-sunken text-ink transition-colors text-left"
    >
      <span className="text-sm font-bold uppercase tracking-wider">{label}</span>
      <span
        className="text-xs transition-transform duration-200 flex-shrink-0"
        style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', display: 'inline-block' }}
      >
        &#9660;
      </span>
    </button>
    {isExpanded && (
      <div className="border-t border-hairline">
        {children}
      </div>
    )}
  </div>
);

const CallInsightsTab: React.FC<CallInsightsTabProps> = ({
  sessionId,
  callInsights,
  salesPlaybookUsage,
  speechRateAlerts,
  isAdmin,
  onAnalysisUpdated,
  onNavigateToTimestamp,
  participantsEditor,
}) => {
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [reanalyzeError, setReanalyzeError] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<'summary' | 'details'>('summary');
  const isMultiParty = isMultiPartyCall(callInsights?.participants);
  const [expandedSection, setExpandedSection] = useState<string | null>(
    isMultiParty ? 'participants' : 'sales_intelligence'
  );
  const [detailsInitialSubSection, setDetailsInitialSubSection] = useState<string | undefined>(undefined);
  const pollCountRef = useRef(0);

  // Multi-party calls open Details on Participants, also when the analysis
  // finishes after the page has loaded.
  useEffect(() => {
    if (isMultiParty) setExpandedSection('participants');
  }, [isMultiParty]);

  const handleSwitchToDetails = useCallback(
    (section: DetailsSection, subSection?: string) => {
      setExpandedSection(section);
      setDetailsInitialSubSection(subSection);
      setActiveSubTab('details');
    },
    []
  );

  const toggleSection = useCallback((id: string) => {
    setExpandedSection((prev) => (prev === id ? null : id));
  }, []);

  // Auto-poll while pending
  const isPending = callInsights?.status === 'pending';
  useEffect(() => {
    if (!isPending) {
      pollCountRef.current = 0;
      return;
    }
    const intervalId = setInterval(() => {
      if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
        clearInterval(intervalId);
        return;
      }
      pollCountRef.current += 1;
      onAnalysisUpdated();
    }, 6000);
    return () => clearInterval(intervalId);
  }, [isPending, onAnalysisUpdated]);

  const handleReanalyze = async () => {
    setIsReanalyzing(true);
    setReanalyzeError(null);
    try {
      const response = await apiService.post(
        `/admin/sessions/${sessionId}/call-insights-reanalyze`
      );
      if (!response.success) {
        throw new Error(response.error ?? 'Failed to start re-analysis');
      }
      onAnalysisUpdated();
    } catch (err: any) {
      setReanalyzeError(err?.message ?? 'An unexpected error occurred.');
    } finally {
      setIsReanalyzing(false);
    }
  };

  const completedDimensions = callInsights?.dimensions_complete ?? [];

  // --- Empty state ---
  if (!callInsights) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg font-semibold text-ink-secondary mb-2">No Analysis Yet</p>
        <p className="text-sm text-ink-muted max-w-sm mb-5">
          Run a Call Insights analysis to get AI-powered insights on knowledge base handling,
          playbook adherence, and sales intelligence.
        </p>
        {isAdmin && (
          <Button onClick={handleReanalyze} disabled={isReanalyzing} variant="primary" type="button">
            {isReanalyzing ? 'Starting Analysis...' : 'Run Analysis'}
          </Button>
        )}
        {reanalyzeError && (
          <p className="mt-3 text-sm text-error">{reanalyzeError}</p>
        )}
      </div>
    );
  }

  // --- Failed state ---
  if (callInsights.status === 'failed') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg font-semibold text-error mb-2">Analysis Failed</p>
        {callInsights.error && (
          <p className="text-sm text-error bg-error-tint border border-error rounded px-4 py-2 max-w-md mb-4">
            {callInsights.error}
          </p>
        )}
        {isAdmin && (
          <Button onClick={handleReanalyze} disabled={isReanalyzing} variant="primary" type="button">
            {isReanalyzing ? 'Starting...' : 'Retry Analysis'}
          </Button>
        )}
        {reanalyzeError && (
          <p className="mt-3 text-sm text-error">{reanalyzeError}</p>
        )}
      </div>
    );
  }

  // --- Pending / Complete state ---
  const completedCount = completedDimensions.length;
  const progressPct = Math.round((completedCount / TOTAL_DIMENSIONS) * 100);

  return (
    <div className="space-y-3">
      {/* Top meta bar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {callInsights.status === 'complete' && (
            <CallQualityBadge
              score={callInsights.call_quality_score}
              breakdown={callInsights.call_quality_breakdown}
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          {reanalyzeError && (
            <span className="text-xs text-error">{reanalyzeError}</span>
          )}
          {isAdmin && callInsights.status === 'complete' && (
            <Button
              onClick={handleReanalyze}
              disabled={isReanalyzing}
              variant="secondary"
              size="sm"
              type="button"
            >
              {isReanalyzing ? 'Re-analyzing...' : 'Re-analyze'}
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar when pending */}
      {isPending && (
        <div className="flex flex-col items-center py-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 border-2 border-signature border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-semibold text-ink-secondary">Analysis in progress...</p>
          </div>
          {completedCount > 0 ? (
            <div className="w-full max-w-xs">
              <div className="flex justify-between text-xs text-ink-muted mb-1">
                <span>
                  {completedCount} of {TOTAL_DIMENSIONS} dimensions complete
                </span>
                <span>{progressPct}%</span>
              </div>
              <ProgressBar value={progressPct} tone="info" />
              <ul className="mt-3 space-y-1 text-left">
                {completedDimensions.map((dim) => (
                  <li key={dim} className="flex items-center gap-1.5 text-xs text-ink-secondary">
                    <span className="text-success font-bold">&#10003;</span>
                    {DIMENSION_LABELS[dim] ?? dim}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-ink-muted max-w-sm text-center">
              The AI is analyzing the session across 5 dimensions. This may take a minute or two.
            </p>
          )}
        </div>
      )}

      {/* Sub-tab toggle + content — only shown once analysis is complete */}
      {callInsights.status === 'complete' && (
        <div className="space-y-4">
          {/* Sub-tab navigation */}
          <div className="flex border-b border-hairline">
            <button
              type="button"
              onClick={() => setActiveSubTab('summary')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeSubTab === 'summary'
                  ? 'border-signature text-signature-strong'
                  : 'border-transparent text-ink-muted hover:text-ink-secondary hover:border-ink-border'
              }`}
            >
              Summary
            </button>
            <button
              type="button"
              onClick={() => setActiveSubTab('details')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeSubTab === 'details'
                  ? 'border-signature text-signature-strong'
                  : 'border-transparent text-ink-muted hover:text-ink-secondary hover:border-ink-border'
              }`}
            >
              Details
            </button>
          </div>

          {/* Summary sub-tab */}
          {activeSubTab === 'summary' && (
            <CallSummaryTab
              sessionId={sessionId}
              callInsights={callInsights}
              salesPlaybookUsage={salesPlaybookUsage}
              speechRateAlerts={speechRateAlerts}
              isAdmin={isAdmin}
              onAnalysisUpdated={onAnalysisUpdated}
              onNavigateToTimestamp={onNavigateToTimestamp}
              onSwitchToDetails={handleSwitchToDetails}
            />
          )}

          {/* Details sub-tab */}
          {activeSubTab === 'details' && (
            <div className="space-y-4">
              {(participantsEditor || hasParticipants(callInsights?.participants)) && (
                <SectionAccordion
                  label="Participants"
                  isExpanded={expandedSection === 'participants'}
                  onToggle={() => toggleSection('participants')}
                >
                  <div className="p-5 space-y-6">
                    {participantsEditor}
                    {callInsights?.participants && <MultiPartyInsights data={callInsights.participants} />}
                  </div>
                </SectionAccordion>
              )}

              <SectionAccordion
                label="Sales Intelligence"
                isExpanded={expandedSection === 'sales_intelligence'}
                onToggle={() => toggleSection('sales_intelligence')}
              >
                <div className="p-5">
                  <SalesIntelligenceV2Section
                    sessionId={sessionId}
                    intelligence={callInsights?.sales_intelligence!}
                    callInsights={callInsights}
                    speechRateAlerts={speechRateAlerts}
                    isAdmin={isAdmin}
                    onReviewSaved={onAnalysisUpdated}
                    onNavigateToTimestamp={onNavigateToTimestamp}
                  />
                </div>
              </SectionAccordion>

              <SectionAccordion
                label="Knowledge Base Handling"
                isExpanded={expandedSection === 'knowledge_base'}
                onToggle={() => toggleSection('knowledge_base')}
              >
                <div className="p-5">
                  <KnowledgeBaseSection
                    sessionId={sessionId}
                    callInsights={callInsights}
                    isAdmin={isAdmin}
                    onReviewSaved={onAnalysisUpdated}
                    onNavigateToTimestamp={onNavigateToTimestamp}
                    initialExpandedAccordion={detailsInitialSubSection}
                  />
                </div>
              </SectionAccordion>

              <SectionAccordion
                label="Playbook"
                isExpanded={expandedSection === 'playbook'}
                onToggle={() => toggleSection('playbook')}
              >
                <div className="p-5">
                  <PlaybookSection
                    sessionId={sessionId}
                    callInsights={callInsights}
                    salesPlaybookUsage={salesPlaybookUsage}
                    isAdmin={isAdmin}
                    onReviewSaved={onAnalysisUpdated}
                  />
                </div>
              </SectionAccordion>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CallInsightsTab;
